<template>
  <div
    class="task-card-detail d-flex flex-column gap-2"
    data-testid="horizon-task-card"
  >
    <div class="task-card-detail__meta">
      <div class="task-card-detail__meta-left">
        <v-checkbox
          v-if="canSelectForDueDateUpdate"
          dense
          hide-details
          class="task-card-detail__checkbox ma-0 pa-0"
          :input-value="selected"
          color="primary"
          data-testid="horizon-task-card__checkbox"
          @change="$emit('selection-change', $event)"
        />

        <span
          v-if="task._id"
          class="task-card-detail__ref task-card-detail__link"
          role="link"
          tabindex="0"
          data-testid="horizon-task-card__ref"
          @click.stop="viewTaskDetail"
          @keydown.enter.stop="viewTaskDetail"
        >
          {{ task.ref }}
        </span>

        <span
          v-else
          class="task-card-detail__ref"
          data-testid="horizon-task-card__ref"
        >
          {{ task.ref }}
        </span>

        <span
          v-if="task.locationName"
          class="task-card-detail__location"
          data-testid="horizon-task-card__location"
        >
          <v-icon x-small class="task-card-detail__location-icon"
            >mdi-map-marker-outline</v-icon
          >

          {{ task.locationName }}
        </span>
      </div>

      <span
        v-if="task.priority"
        class="task-card-detail__priority"
        :class="`task-card-detail__priority--${priorityKey}`"
        data-testid="horizon-task-card__priority"
      >
        {{ task.priority }}
      </span>
    </div>

    <span
      v-if="task._id"
      class="task-card-detail__name two-line-clamp task-card-detail__link"
      role="link"
      tabindex="0"
      data-testid="horizon-task-card__name"
      @click.stop="viewTaskDetail"
      @keydown.enter.stop="viewTaskDetail"
    >
      {{ task.description }}
    </span>

    <span
      v-else
      class="task-card-detail__name two-line-clamp"
      data-testid="horizon-task-card__name"
    >
      {{ task.description }}
    </span>

    <div class="task-card-detail__footer">
      <div
        v-if="task.isOverdue"
        class="task-card-detail__overdue"
        data-testid="horizon-task-card__overdue"
      >
        <span class="task-card-detail__overdue-icon">
          <v-icon x-small class="task-card-detail__overdue-icon-symbol"
            >mdi-clock-outline</v-icon
          >
        </span>

        <span>{{ task.overdueDays }}</span>
      </div>

      <div v-else class="task-card-detail__footer-spacer" />

      <StatusIndicator
        :variant="statusVariant"
        :custom-label="task.statusBadge"
        size="sm"
        data-testid="horizon-task-card__status"
      />
    </div>
  </div>
</template>

<script>
import "./TaskCardDetail.scss";

import StatusIndicator from "@/components/StatusIndicator.vue";

import {
  TASK_STAGE_STATUS_VARIANT,
  TASK_PRIORITY_CSS_MODIFIER,
  TASK_PRIORITY_CSS_MODIFIER_DEFAULT,
  TASK_STAGES,
} from "../../../utils/constants";

import { HORIZON_VIEWS } from "../../../utils/horizonConstants";

import { openTaskDetailPage } from "../../../utils/tasklistUtils";

/** Single task row in the Horizon panel — checkbox, ref, and navigation to task detail. */
export default {
  name: "TaskCardDetail",

  components: {
    StatusIndicator,
  },

  props: {
    task: {
      type: Object,

      required: true,
    },

    selected: {
      type: Boolean,

      default: false,
    },
  },

  computed: {
    /** CSS modifier key for priority badge styling (high/medium/low). */
    priorityKey() {
      return (
        TASK_PRIORITY_CSS_MODIFIER[this.task.priority] ||
        TASK_PRIORITY_CSS_MODIFIER_DEFAULT
      );
    },

    /** StatusIndicator variant derived from task stage. */
    statusVariant() {
      return TASK_STAGE_STATUS_VARIANT[this.task.stage] || "unknown-status";
    },

    /** Approved tasks hide bulk due date checkbox entirely. */
    canSelectForDueDateUpdate() {
      return this.task.stage !== TASK_STAGES.CLOSED;
    },
  },

  methods: {
    /** Navigates to task detail in same tab; back returns to Horizon view. */
    viewTaskDetail() {
      openTaskDetailPage({
        router: this.$router,

        store: this.$store,

        task: this.task,

        checklistId: this.$route.params.id,

        userType:
          this.$route.query.userType || this.$store.state.tasklistUserType,

        tasklistView: HORIZON_VIEWS.HORIZON,
      });
    },
  },
};
</script>
